import * as React from "react";
import * as ToggleButton from 'react-toggle-button';
import { sentenceCase } from "change-case";
import { generateRandomKey } from "../../utils";

import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	ReferenceLine,
	Legend,
	Cell,
	Text
} from 'recharts';

import { IPageProperties } from "../../models";
import { isNullOrUndefined, deepCopy } from "../../utils";

import { PercentLabel } from "./PercentLabel";
import { Disclaimer } from "../../assets";

interface IProps {
	newData: any;
	isMultiAsset: boolean;
	isFixedIncome: boolean;
}

interface IState {
	showAdditionalView?: boolean;
	showGroupedView?: boolean;
}

const styles = require('./styles.pcss');

// Format horizontal barchart for percent
const percentTicks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const percentDomain = [0, 100];

export class StackedBarChart extends React.Component<React.Props<{}> & IPageProperties & IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			showAdditionalView: false,
			showGroupedView: false,
		};

		this.toggleAdditionalView = this.toggleAdditionalView.bind(this);
		this.renderBars = this.renderBars.bind(this);
		this.orchestrateData = this.orchestrateData.bind(this);
		this.renderToggleText = this.renderToggleText.bind(this);
		this.renderToggleButton = this.renderToggleButton.bind(this);
	}

	toggleAdditionalView(value, isGroupedChart) {
		if (isGroupedChart) {
			this.setState(() => ({
				showGroupedView: !this.state.showGroupedView
			}));
		}
		else {
			this.setState(() => ({
				showAdditionalView: !this.state.showAdditionalView
			}));
		}
	}

	renderPercentageLabel(showAdditionalView, dataKey, data) {
		// only show the label after the second bar, (fund bar)
		return <PercentLabel showAdditionalView={showAdditionalView} dataKey={dataKey} data={data}/>
	}

	renderBar(data, dataKey, fill, barSize) {
		const { showAdditionalView } = this.state;
		if (data.length > 0) {
			return (
				<Bar
					stackId={"1"}
					style={{ transform: 'translate(1px, 0)' }}
					dataKey={dataKey}
					fill={fill}
					barSize={barSize}
					label={
						this.renderPercentageLabel(showAdditionalView,dataKey,data)
					}
				>
					{data.map((item, index) => {
						const isSubgroup = !!item.Subgroup;
						const isIndex = dataKey !== "Index";
						const color = isSubgroup && isIndex ? "#cc9933" : fill;

						return <Cell key={`cell-${index}`} fill={color} />;
					})}
				</Bar>
			);
		} else {
			return (<div></div>);
		}

	}

	renderBars(data, tableHeadings, showIndex, isGroupedChart) {
		const { showAdditionalView } = this.state;
		const barSize = 20.08 * 2;
		const barGap = 4;
		data = data.map((item, index) => {
			if (item.Fund) {
				item.DataKeyToUse = 'Fund';
				item.DataIndex = index;
			}
			else if (!item.Fund && item.Net) {
				item.DataKeyToUse = 'Net';
				item.DataIndex = index;
			}

			return item;
		})
		const allowedMainViewKeys = ["net", "net exposure", "fund", "index"];
		const allowedAdditionalViewKeys = ["physical", "cds long", "cds short", "relative weight", "short exposure", "long exposure"];
		let rows = [];
		if (showAdditionalView) {
			const keys = tableHeadings.filter(key => allowedAdditionalViewKeys.includes(key.toLowerCase()));
			const colors = ["#cc9933", "#943840", "#9da939"];
			keys.map((key, index) => {
				rows.push(this.renderBar(data, key, colors[index], barSize / 1));
			});
		}
		else {
			const keys = tableHeadings.filter(key => allowedMainViewKeys.includes(key.toLowerCase()));
			const colors = ["#004f5c", "#9da939"];
			keys.map((key, index) => {
				rows.push(this.renderBar(data, key, colors[index], barSize / keys.length));
			});
		}

		return rows;
	}

	orchestrateData(newData, tableHeadings, tableKeys, showIndex) {
		const { isFixedIncome, isMultiAsset } = this.props;

		let chartData = newData.map(item => {
			let additionalPoints = tableHeadings.reduce((acc, current, currentIndex) => {
				if (!acc[current]) {
					acc[current] = parseFloat(item[tableKeys[currentIndex]]);
				}

				return acc;
			}, {});

			if (!isFixedIncome) {
				let relativeWeightKey = "Relative Weight";
				additionalPoints = {
					...additionalPoints,
					[relativeWeightKey]: item.relative_weight
				};
			}

			if (isMultiAsset) {
				let shortExposure = "Short exposure";
				let longExposure = "Long exposure";
				additionalPoints = {
					...additionalPoints,
					[shortExposure]: item.short_exposure,
					[longExposure]: item.long_exposure
				};
			}

			if (true) {
				let indexKey = "Index";
				additionalPoints = {
					...additionalPoints,
					[indexKey]: item.primary_comparator
				};
			}

			return { ...item, ...additionalPoints };
		});

		return chartData;

	}

	renderToggleText(isGroupedChart, tableHeadings: string[]) {
		const { showAdditionalView, showGroupedView } = this.state;

		let additionalViewText = "";
		if (isGroupedChart) {
			additionalViewText = "subgroups";

			if (showGroupedView) {
				return <span>Hide&nbsp;{additionalViewText}</span>;
			}
			else {
				return <span>Show&nbsp;{additionalViewText}</span>;
			}
		}
		else {
			if (tableHeadings.includes(sentenceCase("Relative weight"))) {
				additionalViewText = "relative weight";
			}
			else if (tableHeadings.includes(sentenceCase("Short exposure"))) {
				additionalViewText = "short and long exposure";
			}
			else if (tableHeadings.includes(sentenceCase("Physical"))) {
				additionalViewText = "physical and CDS exposure split";
			}

			if (showAdditionalView) {
				return <span>Hide&nbsp;{additionalViewText}</span>;
			}
			else {
				return <span>Show&nbsp;{additionalViewText}</span>;
			}
		}
	}

	renderToggleButton(data, isGroupedChart, tableHeadings) {
		const { showAdditionalView, showGroupedView } = this.state;
		const toggleLabelFn = () => null;

		if (isGroupedChart && data.item.length === 0) {
			return null;
		}
		else if (tableHeadings.length === 1 && data.total.length === 0) {
			return null;
		}

		return (
			<div className={styles.toggleButtonContainer}>
				<div className={styles.toggleButton}>
					<ToggleButton
						value={isGroupedChart ? showGroupedView : showAdditionalView}
						onToggle={(value) => this.toggleAdditionalView(value, isGroupedChart)}
						colors={{
							active: {
								base: 'rgb(204, 153, 51);',
							},
							inactive: {
								base: 'rgb(0, 79, 92)',
							}
						}}
						inactiveLabel={toggleLabelFn}
						activeLabel={toggleLabelFn}
					/>
					<p>&nbsp;&nbsp;{this.renderToggleText(isGroupedChart, tableHeadings)}</p>
				</div>
			</div>
		);
	}

	getHeadingData(newData) {
		let datatype = "item";
		if (newData.item.length === 0) {
			datatype = "total";
		}

		const tableHeadings = newData[datatype][0].table_headings.split("|");
		const tableKeys = newData[datatype][0].table_keys.split("|");
		const heading = newData[datatype][0]["heading"];
		const allocType = newData[datatype][0]["allocation_type"];

		return {
			tableHeadings,
			tableKeys,
			heading,
			allocType
		};
	}

	render() {
		const { isFixedIncome, shareClass, newData, appParams } = this.props;
		const { showGroupedView } = this.state;
		// tslint:disable-next-line:variable-name
		const CustomAxisTick = (props) => {
			const { x, y, fill, payload, data, showGroupedView } = props;

			if (showGroupedView) {

				let item = data.item.filter(x => x.label.toLowerCase() === payload.value.toLowerCase() && x.type === "ITEM");
				let total = data.total.filter(x => x.label.toLowerCase() === payload.value.toLowerCase() && x.type === "TOTAL");

				if (total.length > 0 && item.length > 0) {
					return <Text key={generateRandomKey("otherGroup_", 8)} className="otherGroup" x={x} y={y} dy={0} dx={-10} fill={fill} fontSize={8} textAnchor="end" width={100}>{payload.value}</Text>;
				}
				else if (total.length > 0) {
					if (total[0].type === "TOTAL") {
						return <Text key={generateRandomKey("primaryGroup_", 8)} className="primaryGroup" x={x} y={y} dy={8} dx={-10} fill={fill} fontSize={8} textAnchor="end" width={100}>{payload.value}</Text>;
					}
				}
				else if (item.length > 0) {
					if (item[0].type === "ITEM") {
						return <Text key={generateRandomKey("_subGroup_", 8)} className="_subGroup" x={x} y={y} dy={9} dx={-10} fill={fill} fontSize={8} textAnchor="end" width={125}>{payload.value}</Text>;
					}
				}

			} else {
				return <Text key={generateRandomKey("hideSubgroup_", 8)} className="hideSubgroup" x={x} y={y} dy={8} dx={-10} fill={fill} fontSize={8} textAnchor="end" width={100}>{payload.value}</Text>;
			}

		};
		if (isNullOrUndefined(newData)) {
			return null;
		}

		let isGroupedChart = false;
		let { tableHeadings, tableKeys, heading, allocType } = this.getHeadingData(newData);
		const showIndex = true || !isFixedIncome;

		let data = null;
		if (newData.total.length > 0) {
			isGroupedChart = true;
			if (showGroupedView) {
				let rows = [];
				newData.total.map(totalItem => {
					rows.push(totalItem);
					let itemMatchTotal = newData.item.filter(item => item["group_name"] === totalItem["group_name"]);
					if (newData.total[0].allocation_type === "sector") { //SECTOR specific HACK
						itemMatchTotal.map(item => {
							item.Subgroup = item.type;
							rows.push(item);
						});
					}
					else {
						if (itemMatchTotal.length >= 1 && itemMatchTotal[0].label.toLowerCase() !== totalItem.label.toLowerCase()) {
							itemMatchTotal.map(item => {
								item.Subgroup = item.type;
								rows.push(item);
							});
						}
					}
				});
				data = [...rows];
			}
			else {
				data = deepCopy(newData.total);
			}
		}
		else {
			data = deepCopy(newData.item.filter(item => item.type.toLowerCase() === "item"));
		}

		const order = newData.item.reduce((acc, item) => acc + item.display_order, 0);
		const chartData = this.orchestrateData(data, tableHeadings, tableKeys, showIndex);

		return (
			<div key={`${allocType}-${order * 3}`} className={`${styles.colXs12} ${styles.colMd6}`}>
				<h4 className={styles.chartHeader}>
					{heading}
				</h4>
				{this.renderToggleButton(newData, isGroupedChart, tableHeadings)}
				{isGroupedChart && this.renderToggleButton(newData, false, tableHeadings)}
				<div className={styles.stackedBarChart} style={{ height: `${chartData.length * 65}px` }}>
					<ResponsiveContainer minHeight={65}>
						<BarChart
							data={chartData}
							layout="vertical"
							barCategoryGap={100}
							margin={{ top: 20, left: 0, right: 45, bottom: 0 }}
							stackOffset="sign"
							width={400}
						>
							<CartesianGrid
								style={{
									transform: 'translateY(-1px)'
								}}
								strokeWidth="52"
								stroke="#f0f4f5"
								vertical={false}
							/>
							<YAxis
								dataKey="label"
								width={120}
								height={46}
								type="category"
								tickLine={false}
								tick={<CustomAxisTick data={newData} showGroupedView={showGroupedView} />}
							/>
							<XAxis
								type="number"
								padding={{ top: 100 }}
								domain={percentDomain}
								ticks={percentTicks}
								height={100}
								tickCount={10}
								hide={true}
							/>
							<Tooltip cursor={{ fill: '#f1f1f1' }} />
							{this.renderBars(chartData, tableHeadings, showIndex, isGroupedChart)}
							<Legend />
						</BarChart>
					</ResponsiveContainer>
				</div>
				<Disclaimer
					appParams={appParams}
					disclaimer={shareClass.disclaimers[`breakdown_definition_${chartData[0].allocation_type}_${appParams.clienttype}_0`]}
				/>
				<br />
			</div>
		);
	}
}


// WEBPACK FOOTER //
// ./src/client/components/PageAllocations/StackedBarChart.tsx